// Private Route Component - Enhanced with centralized permission system
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import { isLogin, canAccessRoute, getFirstAvailableRoute } from '../../utils/permissions';

/**
 * ForbiddenPage - Shown when user doesn't have permission to access a route
 * Can be replaced later with a custom designed 403 page
 */
const ForbiddenPage = () => {
  const firstRoute = getFirstAvailableRoute();
  const redirectPath = firstRoute?.path || '/';

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '60vh',
      padding: '20px'
    }}>
      <Result
        status="403"
        title="403"
        subTitle="Sorry, you are not authorized to access this page."
        extra={
          <Button type="primary" href={redirectPath}>
            Go to {firstRoute?.label || 'Home'}
          </Button>
        }
      />
    </div>
  );
};

/**
 * PrivateRoute - Route wrapper with authentication and permission checks
 * 
 * Features:
 * 1. Checks if user is logged in → redirects to /login if not
 * 2. Checks if user has permission to access the route → shows 403 page if not
 * 3. Route permissions are defined in ROUTES config in permissions.js
 * 
 * Usage:
 * // Basic usage - wraps layout, checks both auth and route permissions
 * <Route path="/" element={
 *   <PrivateRoute>
 *     <AppLayout />
 *   </PrivateRoute>
 * } />
 * 
 * // The route permission check happens automatically based on the current path
 * // It reads from ROUTES config in permissions.js to determine which module
 * // the route belongs to and checks if user has access to that module
 */
const PrivateRoute = ({ children }) => {
  const location = useLocation();
  const loggedIn = isLogin();

  // Not logged in - redirect to login
  if (!loggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check route permission using centralized canAccessRoute()
  // This reads from ROUTES config in permissions.js
  if (!canAccessRoute(location.pathname)) {
    // If user is on base URL "/" but lacks Dashboard access, redirect to first accessible page
    if (location.pathname === '/') {
      const firstRoute = getFirstAvailableRoute();
      if (firstRoute?.path && firstRoute.path !== '/') {
        return <Navigate to={firstRoute.path} replace />;
      }
    }
    return <ForbiddenPage />;
  }

  return children;
};

export default PrivateRoute;
